<template>
  <div class="invoice__items_details">
    <!-- Return Quantity Data Listing -->
    <vs-table class="mt-5" :data="salesItemsComputed">
      <template slot="thead">
        <vs-th>
          <vs-checkbox class="mx-auto" :checked="selectedAll" @input="(e) => changeInput()"></vs-checkbox>
        </vs-th>
        <vs-th>Item Name</vs-th>
        <vs-th>Unit Price</vs-th>
        <vs-th>Sold Qty</vs-th>
        <vs-th>Return Quantity</vs-th>
        <vs-th>Tax</vs-th>
        <vs-th>Discount Amount</vs-th>
        <vs-th>Total Amount</vs-th>
      </template>

      <template slot-scope="{ data }">
        <vs-tr :key="i" v-for="(tr, i) in data">
          <vs-td><vs-checkbox ref="itemCheck" v-model="tr.return_status" @input="(e) => changeStatusOfSelectAll(e)"></vs-checkbox></vs-td>
          <vs-td>{{ tr.product_name }}</vs-td>
          <vs-td>$ {{ tr.price_per_unit | currencyFilter }}</vs-td>
          <vs-td>{{ tr.sales_qty | weightFilter }}</vs-td>
          <vs-td>
            <vs-input-number
              ref="itemReturnQty"
              v-if="![categories.PLANT, categories.MATURE].includes(tr.product.categoryId)"
              :id="tr.id"
              :value="tr.return_qty"
              :max-decimal="1"
              :step="tr.product.categoryId === categories.FLOWER || tr.product.categoryId === categories.SHAKE ? 0.5 : 1"
              :min="0"
              :max="tr.sales_qty"
              @input="validateWeightType(i, $event)"
            />

            <vx-input-group v-else class="w-24">
              <vs-input disabled v-model="tr.return_qty" />
              <template slot="append">
                <div class="append-text btn-addon">
                  <vs-button icon-pack="feather" icon="icon-maximize-2" @click="selectReturnPlants(tr.id)" />
                </div>
              </template>
            </vx-input-group>
          </vs-td>
          <vs-td>$ {{ tr.tot_taxes_amt | currencyFilter }}</vs-td>
          <vs-td>$ {{ tr.tot_discount | currencyFilter }}</vs-td>
          <vs-td>$ {{ tr.tot_price | currencyFilter }}</vs-td>
        </vs-tr>
      </template>
    </vs-table>
    <!-- End Return Quantity Data Listing  -->

    <!-- Old Return Data List && Grand Total -->
    <div class="vx-row pt-5 px-5">
      <div class="vx-col w-full md:w-1/2 px-8 mt-5">
        <div class="vx-row mb-2">
          <vs-input v-if="saleDetails" icon="icon icon-file-text" icon-pack="feather" class="w-full" label="Note" name="Note" v-model="saleDetails.return_note" />
        </div>
      </div>

      <vs-table hoverFlat class="w-1/2 ml-auto mt-4" :data="[{}]">
        <vs-tr>
          <vs-th class="pointer-events-none">SUBTOTAL</vs-th>
          <vs-td>$ {{ invoice_subTotal | currencyFilter }}</vs-td>
        </vs-tr>
        <vs-tr>
          <vs-th class="pointer-events-none">DISCOUNT ON ALL </vs-th>
          <vs-td>$ {{ invoice_discount | currencyFilter }}</vs-td>
        </vs-tr>
        <vs-tr>
          <vs-th class="pointer-events-none">TAX</vs-th>
          <vs-td>$ {{ invoice_tax | currencyFilter }}</vs-td>
        </vs-tr>
        <vs-tr v-if="saleDetails && saleDetails.redeem_dollar > 0">
          <vs-th class="pointer-events-none">REDEEM AMOUNT</vs-th>
          <vs-td>$ {{ saleDetails.redeem_dollar | currencyFilter }}</vs-td>
        </vs-tr>
        <vs-tr>
          <vs-th class="pointer-events-none">GRAND TOTAL</vs-th>
          <vs-td>$ {{ invoice_grand_total | currencyFilter }}</vs-td>
        </vs-tr>
      </vs-table>
    </div>
    <!-- End Old Return Data List && Grand Total-->

    <plant-popup :is_model_open.sync="is_popup_active_select_plant_batch" :products_active_plants.sync="selected_items_plant" @return_plants="return_plants" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import PlantPopup from './PlantsPopup.vue'
import { weightMixins } from '@/utils/weightType'
import { paymentMixins } from '@/utils/paymentTypes'

export default {
  name: 'InvoiceItemDetails',

  // props
  props: {
    saleItems: Array,
    saleDetails: Object
  },

  data() {
    return {
      is_popup_active_select_plant_batch: false,
      selected_item_id: null,
      selected_items_plant: [],
      selectAllStatus: false
    }
  },

  // mounted property
  async mounted() {
    this.saleReturn =
      this.salesItems &&
      this.salesItems.map((item) => ({
        ...item,
        return_plants_id: []
      }))
  },

  mixins: [paymentMixins, weightMixins],

  // methods
  methods: {
    // select all items for return
    changeInput() {
      this.selectAllStatus = !this.selectAllStatus
      this.salesItemsComputed.map((item) => {
        item.return_status = !!this.selectAllStatus
      })
    },

    changeStatusOfSelectAll(returnStatus) {
      if (this.selectAllStatus && !returnStatus) {
        this.selectAllStatus = false
      }
    },

    // select all plants of selected item
    async selectReturnPlants(id) {
      this.selected_item_id = id
      // open model
      this.is_popup_active_select_plant_batch = !this.is_popup_active_select_plant_batch

      // select item plants
      const selected_item = this.saleItems.find((element) => element.id === id)
      this.selected_items_plant = selected_item.salesPlants
    },

    // return plants of selected item
    async return_plants(plants) {
      // find the index from invoice items array
      const tempIndex = this.saleItems.findIndex((element) => element.id === this.selected_item_id)

      // if index found - set the quantity and add the selected plants
      if (tempIndex > -1) {
        this.saleItems[tempIndex].return_qty = plants.length

        if (plants.length) {
          this.saleItems[tempIndex].return_plants_id = plants
        }
      }
    },

    // validate weight types - and increase quantity
    async validateWeightType(index, value) {
      const val = [this.categories.FLOWER, this.categories.SHAKE].includes(this.salesItemsComputed[index].product.categoryId) ? Math.abs(value) : Math.ceil(Math.abs(value))

      if (this.salesItemsComputed[index].sales_qty < val) {
        this.salesItemsComputed[index].return_qty = weightMixins.methods.mathRound(this.salesItemsComputed[index].sales_qty, 3)
      } else {
        this.salesItemsComputed[index].return_qty = val < 0.0 ? 0 : weightMixins.methods.mathRound(val, 3)
      }
    }
  },

  // computed property
  computed: {
    ...mapState('product', ['loading', 'categories']),

    console: () => console,

    selectedAll() {
      const returnItems = this.salesItemsComputed.filter((item) => item.return_status)
      return returnItems.length === this.salesItemsComputed.length
    },

    // calculate sub_total (invoice) = qty * price
    invoice_subTotal() {
      return this.saleItems.reduce((acc, item) => acc + item.tot_price, 0)
    },

    salesItemsComputed() {
      return this.saleItems.reduce((acc, item) => {
        const items = acc
        if (item.sales_qty !== 0) {
          items.push(item)
        }
        return items
      }, [])
    },

    // invoice discount
    invoice_discount() {
      return this.saleItems.reduce((acc, item) => acc + item.tot_discount, 0)
    },

    // invoice tax
    invoice_tax() {
      return this.saleItems.reduce((acc, item) => acc + item.tot_taxes_amt, 0)
    },

    // invoice grand total = sub total - discount + tax
    invoice_grand_total() {
      return this.invoice_subTotal + this.invoice_tax
    }
  },

  // components
  components: {
    PlantPopup
  },

  filters: {
    // currencyFilter rounded to 2 point
    currencyFilter(value) {
      return paymentMixins.methods.mathRound(Number(value), 2)
    },

    //weightFilter rounded conversation
    weightFilter(value) {
      return weightMixins.methods.mathRound(Number(value), 3)
    }
  },

  beforeRouteLeave(_, __, next) {
    this.is_popup_active_select_plant_batch = false
    next()
  }
}
</script>

<style lang="scss">
.invoice__items_details {
  input:focus + span.checkbox_x {
    &.vs-checkbox {
      border: 2px solid green !important;
    }
  }
}
</style>
